import React, { useState, useCallback, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag as AntTag,
  Popconfirm,
  Typography,
  Card,
  Modal,
  Form,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { Tag } from '../../types';
import { tagsApi, CreateTagRequest, UpdateTagRequest } from '../../services/api';
import { useApi, useAsyncOperation } from '../../hooks';
import { formatDate, truncateText } from '../../utils';
import { PAGE_SIZE, TAG_STATUS } from '../../constants';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;

const Tags: React.FC = () => {
  // For Tags, we don't need useSearchParams as it's simpler, but we'll keep the pattern consistent
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [updatingTagId, setUpdatingTagId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();

  // Memoize API function
  const getTags = useCallback(() => tagsApi.getTags({
    pageIndex: currentPage - 1,
    pageSize: PAGE_SIZE,
    name: searchText || undefined,
    status: activeFilter === undefined ? undefined : activeFilter ? TAG_STATUS.ACTIVE : TAG_STATUS.INACTIVE,
  }), [currentPage, searchText, activeFilter]);

  // Fetch tags
  const {
    data: tagsData,
    loading: tagsLoading,
    refresh: refreshTags,
  } = useApi(getTags, { immediate: true });

  // Tự động fetch khi filter hoặc trang thay đổi
  useEffect(() => {
    refreshTags();
  }, [refreshTags, searchText, activeFilter, currentPage]);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  }, []);

  const handleFilterChange = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const handleReset = useCallback(() => {
    setSearchText('');
    setActiveFilter(undefined);
    setCurrentPage(1);
    form.resetFields();
    refreshTags();
  }, [form, refreshTags]);

  const handleCreate = () => {
    setEditingTag(null);
    form.resetFields();
    form.setFieldsValue({ color: '#1890ff' }); // Default color
    setIsModalVisible(true);
  };

  const handleEdit = (tag: Tag) => {
    setEditingTag(tag);
    form.setFieldsValue({
      name: tag.name,
      description: tag.description,
      status: tag.status,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    await executeAsync(
      async () => {
        await tagsApi.deleteTag(id);
      },
      { successMessage: 'Tag deleted successfully!' }
    );
    refreshTags();
  };

  const handleToggleActive = async (checked: boolean, tag: Tag) => {
    setUpdatingTagId(tag.id);
    const newStatus = checked ? TAG_STATUS.ACTIVE : TAG_STATUS.INACTIVE;
    const operation = async () => {
      const response = await tagsApi.updateTag(tag.id, { status: newStatus });
      return 'data' in response ? response.data : response;
    };

    const result = await executeAsync(
      operation,
      { 
        successMessage: checked
          ? 'Tag activated successfully!' 
          : 'Tag deactivated successfully!' 
      }
    );
    setUpdatingTagId(null);
    if (result) {
      refreshTags();
    }
  };

  const handleSubmit = async (values: CreateTagRequest) => {
    // Convert color picker value to hex string
    const processedValues = values;

    const operation = editingTag
      ? async () => {
          const response = await tagsApi.updateTag(editingTag.id, processedValues as UpdateTagRequest);
          return 'data' in response ? response.data : response;
        }
      : async () => {
          const response = await tagsApi.createTag(processedValues);
          return 'data' in response ? response.data : response;
        };

    const result = await executeAsync(
      operation,
      { 
        successMessage: editingTag 
          ? 'Tag updated successfully!' 
          : 'Tag created successfully!' 
      }
    );

    if (result) {
      setIsModalVisible(false);
      form.resetFields();
      refreshTags();
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Tag) => (
        <AntTag color="blue" style={{ fontSize: 14, padding: '4px 8px' }}>
          {text}
        </AntTag>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text ? truncateText(text, 80) : 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: Tag) => (
        <Space>
          <Switch
            checked={status === TAG_STATUS.ACTIVE}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
            loading={updatingTagId === record.id && asyncLoading}
            onChange={(checked) => handleToggleActive(checked, record)}
          />
        </Space>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Tag) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit"
          />
          <Popconfirm
            title="Are you sure you want to delete this tag?"
            description="This action cannot be undone and will remove the tag from all stories."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header Section */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 16,
      }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Tags
          </Title>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size="large"
          style={{
            background: '#1890ff',
            borderRadius: '6px',
            height: '40px',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          Create
        </Button>
      </div>

      {/* Filter Card */}
      <Card
        style={{
          marginBottom: 24,
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
        }}
      >
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 16,
          marginBottom: 16,
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: '500', fontSize: '14px' }}>
              Name
            </label>
            <Search
              placeholder="Search by name..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: '500', fontSize: '14px' }}>
              Status
            </label>
            <Select
              placeholder="Select status"
              allowClear
              value={activeFilter}
              onChange={(value) => {
                setActiveFilter(value);
                handleFilterChange();
              }}
              style={{ width: '100%' }}
            >
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button 
            onClick={handleReset}
            style={{
              borderRadius: '6px',
              height: '36px',
              fontSize: '14px',
            }}
          >
            Reset
          </Button>
        </div>
      </Card>

      {/* Table Card */}
      <Card
        style={{
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
        }}
      >
        <Table
          columns={columns}
          dataSource={tagsData?.data || []}
          rowKey="id"
          loading={tagsLoading}
          pagination={{
            current: currentPage,
            total: tagsData?.totalRecord || 0,
            pageSize: PAGE_SIZE,
            onChange: (page) => {
              setCurrentPage(page);
            },
            showSizeChanger: false,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          scroll={{ x: 1000 }}
          style={{
            fontSize: '14px',
          }}
        />
      </Card>

      <Modal
        title={editingTag ? 'Edit Tag' : 'Create New Tag'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Tag Name"
            rules={[
              { required: true, message: 'Please enter tag name!' },
              { max: 50, message: 'Tag name cannot exceed 50 characters!' },
            ]}
          >
            <Input placeholder="Enter tag name..." />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Enter tag description..." />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={asyncLoading}
              >
                {editingTag ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Tags;


